'use client';

import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import AutoScroll from 'embla-carousel-auto-scroll';
import { Carousel } from '@/components/ui/Carousel';
import { AuditorCard } from '@/components/landing/AuditorCard';
import { CertificationCard } from '@/components/landing/CertificationCard';
import { NewsCard } from '@/components/news';

import {
  User,
  AuditorListItem,
  Certification,
  // Country,
  WeightProfileListItem,
  api,
  News,
  HeroSlide,
  HeroLogo,
  HeroLogoConfig,
} from '@/lib/api';
import HeroSlideshow from '@/components/landing/HeroSlideshow';


export default function HomePage() {
  const t = useTranslations('landing');
  const locale = useLocale();
  const [user, setUser] = useState<User | null>(null);
  const [auditors, setAuditors] = useState<AuditorListItem[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [countries, setCountries] = useState<any[]>([]); // Using any[] to match api response temporarily
  const [weightProfiles, setWeightProfiles] = useState<WeightProfileListItem[]>([]);
  const [latestNews, setLatestNews] = useState<News[]>([]);
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [logos, setLogos] = useState<HeroLogo[]>([]);
  const [logoConfig, setLogoConfig] = useState<HeroLogoConfig>({ alignment: 'center', logo_height: 40 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          currentUser,
          auditorList,
          certList,
          countryList,
          kpDefaults,
          newsList,
          slideList,
          logoList,
          logoConfigData,
        ] = await Promise.all([
          api.getCurrentUser().catch(() => null),
          api.getAuditors().catch(() => []),
          api.getCertifications().catch(() => []),
          api.getCountries().catch(() => []),
          api.getWeightProfiles().catch(() => []),
          api.getNews(10).catch(() => []),
          api.getSlides().catch(() => []),
          api.getLogos().catch(() => []),
          api.getLogoConfig().catch(() => ({ alignment: 'center' as const, logo_height: 40 })),
        ]);

        setUser(currentUser);
        setAuditors(auditorList || []);

        // Filter for certified status (though currently just taking all for demo if needed, but plan said filter)
        // Since getCertifications returns CertificationListItem which has status string
        // We cast to Certification compatible for now or update type if needed.
        // Actually getCertifications returns CertificationListItem[]
        // We need to be careful with types here. 
        // Let's use any for now to avoid strict type mismatch if API types are slightly different in list vs detail
        // but CertificationListItem has status.

        const certifiedCerts = (certList as any[] || []).filter((c: any) => c.status === 'certified');
        setCertifications(certifiedCerts);

        setCountries(countryList || []);
        setWeightProfiles(kpDefaults || []);
        setLatestNews(newsList || []);
        setSlides(slideList || []);
        setLogos(logoList || []);
        if (logoConfigData) setLogoConfig(logoConfigData);
      } catch (error) {
        console.error('Error fetching landing data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCountryName = (code: string) => {
    const country = countries.find(c => c.code === code);
    return country ? (locale === 'pt' ? country.name_pt : country.name_en) : code;
  };

  const getWeightProfileName = (id: string | null) => {
    if (!id) return '';
    const profile = weightProfiles.find(p => p.id === id);
    return profile ? (locale === 'pt' ? profile.name_pt : profile.name_en) : '';
  };

  // Helper to get main image from certification
  const getMainImage = (cert: any) => {
    if (cert.main_image_url) {
      return cert.main_image_url;
    }
    if (cert.files && Array.isArray(cert.files)) {
      return cert.files.find((f: any) => f.main)?.download_url || null;
    }
    // If list item doesn't have files, we might need to fetch details or use a placeholder
    // For now assuming list item might be enriched or we accept null
    return null;
  };

  const isAuthenticated = user !== null;

  return (
    <main className="min-h-screen">
      {/* Hero Slideshow */}
      <HeroSlideshow
        slides={slides}
        logos={logos}
        logoConfig={logoConfig}
        isRoot={user?.role === 'root'}
        onSlidesChange={setSlides}
        onLogosChange={setLogos}
        onLogoConfigChange={setLogoConfig}
      />

      {/* News Section */}
      <section className="py-5   bg-white">
        <div className="max-w-8xl mx-auto px-6 md:px-10">
          <div className="mb-10 flex items-start justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-2">
                {t('newsSection.title')}
              </h2>
            </div>
            <Link href="/news" className="text-slate-900 hover:text-slate-600 font-medium text-sm whitespace-nowrap ml-4">
              {t('newsSection.seeAll')}
            </Link>
          </div>

          {!isLoading && latestNews.length > 0 ? (
            <Carousel
              className="w-full"
              options={{ loop: true, align: 'start', dragFree: true }}
              plugins={[
                AutoScroll({ speed: 0.5, stopOnInteraction: false })
              ]}
              showArrows={false}
            >
              {latestNews.map((news) => (
                <div key={news.id} className="h-[280px] pr-6 flex-[0_0_360px]">
                  <NewsCard
                    title={news.title}
                    description={news.description}
                    date={news.published_at}
                    imageUrl={news.image_url || '/placeholder-news.jpg'}
                    slug={news.slug}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div className="text-center py-10 text-slate-400 italic bg-white border border-slate-200 rounded-sm">
              {isLoading ? t('newsSection.loading') : t('newsSection.noNews')}
            </div>
          )}
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-5 bg-white">
        <div className="max-w-8xl mx-auto px-6 md:px-10">
          <div className="mb-10 flex items-start justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-2">
                {t('certificationsSection.title')}
              </h2>
              <p className="text-slate-500">
                {t('certificationsSection.subtitle')}
              </p>
            </div>
            <Link href="/certifications-showcase" className="text-slate-900 hover:text-slate-600 font-medium text-sm whitespace-nowrap ml-4">
              See all
            </Link>
          </div>

          {!isLoading && certifications.length > 0 ? (
            <Carousel
              className="w-full"
              options={{ loop: true, align: 'start', dragFree: true }}
              plugins={[
                AutoScroll({ speed: 0.5, stopOnInteraction: false, direction: 'backward' })
              ]}
              showArrows={false}
            >
              {certifications.map((cert: any) => (
                <div key={cert.id} className="h-[280px] pr-6 flex-[0_0_320px]">
                  <CertificationCard
                    id={cert.id}
                    name={cert.name || 'Untitled Project'}
                    buildingType={getWeightProfileName(cert.weight_profile_id)}
                    imageUrl={getMainImage(cert)}
                    siteLocation={`${cert.site_city || ''}${cert.site_city && cert.site_country ? ', ' : ''}${getCountryName(cert.site_country || '')}`}
                    offsiteLocation={`${cert.offsite_city || ''}${cert.offsite_city && cert.offsite_country ? ', ' : ''}${getCountryName(cert.offsite_country || '')}`}
                    status={cert.status}
                    updatedAt={cert.updated_at}
                    creatorName={cert.creator_name}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div className="text-center py-10 text-slate-400 italic bg-white border border-slate-200 rounded-sm">
              {isLoading ? t('certificationsSection.loading') : t('certificationsSection.noCertifications')}
            </div>
          )}
        </div>
      </section>

      {/* Auditors Section */}
      <section className="py-5 bg-white">
        <div className="max-w-8xl mx-auto px-6 md:px-10">
          <div className="mb-10 flex items-start justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-2">
                {t('auditorsSection.title')}
              </h2>
              <p className="text-slate-500">
                {t('auditorsSection.subtitle')}
              </p>
            </div>
            <Link href="/auditors" className="text-slate-900 hover:text-slate-600 font-medium text-sm whitespace-nowrap ml-4">
              See all
            </Link>
          </div>

          {!isLoading && auditors.length > 0 ? (
            <Carousel
              className="w-full"
              options={{ loop: true, align: 'start', dragFree: true }}
              plugins={[
                AutoScroll({ speed: 0.5, stopOnInteraction: false })
              ]}
              showArrows={false}
            >
              {auditors.map((auditor) => (
                <div key={auditor.id} className="h-[280px] pr-6 flex-[0_0_280px]">
                  <AuditorCard
                    id={auditor.id}
                    firstName={auditor.first_name}
                    lastName={auditor.last_name}
                    city={auditor.city}
                    country={getCountryName(auditor.country)}
                    profilePictureUrl={api.getProfilePictureUrl(auditor.id)}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div className="text-center py-10 text-slate-400 italic bg-white border border-slate-200 rounded-sm">
              {isLoading ? t('auditorsSection.loading') : t('auditorsSection.noAuditors')}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
